import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, Users, Eye, Star, MessageSquare, TrendingUp, 
  Award, RefreshCw, ArrowLeft, Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color = "red" }) => (
    <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full bg-${color}-600/20 flex items-center justify-center`}>
          <Icon className={`text-${color}-500`} size={20} />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subValue && <p className="text-gray-500 text-sm mt-1">{subValue}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20 flex justify-center items-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { overview, top_models_by_views, top_models_by_rating, category_distribution, daily_views } = analytics || {};

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/ews-admin-panel"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-4"
            >
              <ArrowLeft size={18} /> Back to Admin
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-red-500 uppercase flex items-center gap-3">
              <BarChart3 size={32} /> Analytics Dashboard
            </h1>
          </div>
          <button
            onClick={fetchAnalytics}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12">
          <StatCard icon={Users} label="Total Models" value={overview?.total_models || 0} subValue={`${overview?.pending_models || 0} pending`} />
          <StatCard icon={Star} label="Featured" value={overview?.featured_models || 0} color="yellow" />
          <StatCard icon={Eye} label="Total Views" value={overview?.total_views?.toLocaleString() || 0} subValue={`${overview?.weekly_views || 0} this week`} />
          <StatCard icon={TrendingUp} label="Monthly Views" value={overview?.monthly_views?.toLocaleString() || 0} color="green" />
          <StatCard icon={MessageSquare} label="Enquiries" value={overview?.total_enquiries || 0} subValue={`${overview?.weekly_enquiries || 0} this week`} color="blue" />
          <StatCard icon={Star} label="Avg Rating" value={overview?.average_rating || 0} subValue={`${overview?.total_reviews || 0} reviews`} color="yellow" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Daily Views Chart */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Calendar size={20} className="text-red-500" /> Views This Week
            </h2>
            <div className="flex items-end justify-between h-48 gap-2">
              {daily_views?.map((day, idx) => {
                const maxViews = Math.max(...daily_views.map(d => d.views), 1);
                const height = (day.views / maxViews) * 100;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                    <span className="text-gray-400 text-xs">{day.views}</span>
                    <div 
                      className="w-full bg-red-600 rounded-t transition-all hover:bg-red-500"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                    <span className="text-gray-500 text-xs">{day.day}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Category Distribution */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BarChart3 size={20} className="text-red-500" /> Models by Category
            </h2>
            <div className="space-y-3">
              {category_distribution?.slice(0, 8).map((cat, idx) => {
                const maxCount = Math.max(...category_distribution.map(c => c.count), 1);
                const width = (cat.count / maxCount) * 100;
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300">{cat.category}</span>
                      <span className="text-gray-500">{cat.count}</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full transition-all"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Models by Views */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-green-500" /> Top Models by Views (This Month)
            </h2>
            {top_models_by_views?.length > 0 ? (
              <div className="space-y-4">
                {top_models_by_views.map((model, idx) => (
                  <Link 
                    key={model.id}
                    to={`/models/${model.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-amber-700 text-white' :
                      'bg-zinc-700 text-white'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800">
                      {model.image_url && (
                        <img 
                          src={`${BACKEND_URL}${model.image_url}`}
                          alt={model.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-grow">
                      <p className="text-white font-medium">{model.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">{model.views}</p>
                      <p className="text-gray-500 text-xs">views</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No view data yet</p>
            )}
          </div>

          {/* Top Models by Rating */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Award size={20} className="text-yellow-500" /> Top Rated Models
            </h2>
            {top_models_by_rating?.length > 0 ? (
              <div className="space-y-4">
                {top_models_by_rating.map((model, idx) => (
                  <Link 
                    key={model.id}
                    to={`/models/${model.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-amber-700 text-white' :
                      'bg-zinc-700 text-white'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800">
                      {model.image_url && (
                        <img 
                          src={`${BACKEND_URL}${model.image_url}`}
                          alt={model.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-grow">
                      <p className="text-white font-medium">{model.name}</p>
                      <p className="text-gray-500 text-xs">{model.review_count} reviews</p>
                    </div>
                    <div className="text-right flex items-center gap-1">
                      <Star size={16} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-white font-bold">{model.avg_rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No reviews yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
